import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  TextField,
  Button,
  FormControl,
  CircularProgress,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  Autocomplete,
  Chip,
  FormHelperText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Stack,
  Tooltip,
  IconButton,
  Fade,
  alpha,
} from "@mui/material";
import OrbLoader from "../../components/common/OrbLoader";
import { useTranslation } from "react-i18next";
import OrganizationTypesField from "../../components/company/OrganizationTypesField";
import PageContainer from "../../components/layout/PageContainer";
import { useNavigate, useParams } from "react-router-dom";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import DeleteIcon from "@mui/icons-material/Delete";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import UndoIcon from "@mui/icons-material/Undo";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SaveIcon from "@mui/icons-material/Save";
import {
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useGetCompanyByIdQuery,
  useAssignUsersToCompanyMutation,
  useRemoveUsersFromCompanyMutation,
} from "../../store/apis/companyApi";
import { useGetUsersQuery } from "../../store/apis/usersApi";
import { useDispatch, useSelector } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { Company } from "../../types/CompanyTypes";
import { BillingManagement } from "../../components/billing";
import { AudienceManagement } from "../../components/audiences";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import { BRAND_PILL_INK } from "../../theme/theme";
import * as Sentry from "@sentry/react";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import NumberField from "../../components/common/NumberField";
import { useGoBack } from "../../hooks/useGoBack";

// Define a more comprehensive interface for the form data
interface CompanyFormData extends Omit<
  Partial<Company>,
  "feeVariable" | "feeFixed"
> {
  feeVariable?: number | string;
  feeFixed?: number | string;
}

// Campos que la empresa no puede guardar vacíos, en el orden en que salen en
// pantalla. Los datos fiscales y de contacto no están aquí: viven en el billing,
// que se edita más abajo con su propio formulario.
const REQUIRED_FIELDS = ["nameCommercial", "nameFiscal", "nif"] as const;

type RequiredField = (typeof REQUIRED_FIELDS)[number];

// Un campo por plataforma, todos con el mismo comportamiento: lista de IDs.
const INTEGRATION_FIELDS = [
  { key: "dv360AdvertiserIds", label: "company.dv360AdvertiserId", fallback: "DV360 Advertiser IDs" },
  { key: "outbrainAccountIds", label: "company.outbrainAccountId", fallback: "Outbrain Account IDs" },
  { key: "taboolaAccountIds", label: "company.taboolaAccountId", fallback: "Taboola Account IDs" },
  { key: "spotifyAdAccountIds", label: "company.spotifyAdAccountId", fallback: "Spotify Ad Account IDs" },
  { key: "hivestackAdvertiserIds", label: "company.hivestackAdvertiserId", fallback: "Hivestack Advertiser IDs" },
  { key: "amazonAdvertiserIds", label: "company.amazonAdvertiserId", fallback: "Amazon DSP Advertiser IDs" },
  { key: "metaAdAccountIds", label: "company.metaAdAccountId", fallback: "Meta Ad Account IDs" },
  { key: "tiktokAdvertiserIds", label: "company.tiktokAdvertiserId", fallback: "TikTok Advertiser IDs" },
  { key: "googleAdsCustomerIds", label: "company.googleAdsCustomerId", fallback: "Google Ads Customer IDs" },
  { key: "redditAdAccountIds", label: "company.redditAdAccountId", fallback: "Reddit Ad Account IDs" },
] as const;

type IntegrationField = (typeof INTEGRATION_FIELDS)[number]["key"];

// Campos de la empresa que guarda el botón de abajo. Billing, audiencias y
// usuarios se guardan por su cuenta y no cuentan como cambios pendientes.
const TRACKED_FIELDS = [
  "imageBase64",
  "nameCommercial",
  "organizationTypes",
  "nameFiscal",
  "nif",
  "feeVariable",
  "feeFixed",
  "paymentTerms",
  ...INTEGRATION_FIELDS.map((field) => field.key),
] as const;

type TrackedField = (typeof TRACKED_FIELDS)[number];

const FIELD_LABELS: Record<TrackedField, [string, string]> = {
  imageBase64: ["company.logo", "Logo"],
  nameCommercial: ["company.nameCommercial", "Commercial name"],
  organizationTypes: ["company.organizationTypes", "Organization type"],
  nameFiscal: ["company.nameFiscal", "Fiscal name"],
  nif: ["company.nif", "NIF"],
  feeVariable: ["company.feeVariable", "Variable fee"],
  feeFixed: ["company.feeFixed", "Fixed fee"],
  paymentTerms: ["company.paymentTerms", "Payment terms"],
  ...(Object.fromEntries(
    INTEGRATION_FIELDS.map((field) => [field.key, [field.label, field.fallback]])
  ) as Record<IntegrationField, [string, string]>),
};

const EMPTY_FORM: CompanyFormData = {
  nameCommercial: "",
  organizationTypes: [],
  nameFiscal: "",
  nif: "",
  feeVariable: "0.00",
  feeFixed: "0.00",
  paymentTerms: "",
  logoUrl: "",
  imageBase64: "",
  ...(Object.fromEntries(
    INTEGRATION_FIELDS.map((field) => [field.key, [] as string[]])
  ) as Record<IntegrationField, string[]>),
};

const toFormData = (company: Company): CompanyFormData => ({
  nameCommercial: company.nameCommercial,
  organizationTypes: company.organizationTypes ?? [],
  nameFiscal: company.nameFiscal,
  nif: company.nif,
  feeVariable:
    company.feeVariable != null
      ? Number(company.feeVariable).toFixed(2)
      : "0.00",
  feeFixed:
    company.feeFixed != null ? Number(company.feeFixed).toFixed(2) : "0.00",
  paymentTerms: company.paymentTerms ?? "",
  logoUrl: company.logoUrl,
  imageBase64: company.imageBase64 || "",
  ...(Object.fromEntries(
    INTEGRATION_FIELDS.map((field) => [field.key, company[field.key] || []])
  ) as Record<IntegrationField, string[]>),
});

const parseFee = (value: unknown) => {
  const parsed = parseFloat(String(value ?? "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : 0;
};

// "10" y "10.00" o "Acme " y "Acme" son el mismo valor: no cuentan como cambio.
const normalize = (field: TrackedField, value: unknown) => {
  if (field === "feeVariable" || field === "feeFixed") return parseFee(value);
  if (Array.isArray(value)) return value;
  return String(value ?? "").trim();
};

const isSameValue = (field: TrackedField, a: unknown, b: unknown) =>
  JSON.stringify(normalize(field, a)) === JSON.stringify(normalize(field, b));

// Al pegar "123, 456 789" se crean tres IDs; sin vacíos ni repetidos.
const splitIds = (values: string[]) =>
  Array.from(
    new Set(
      values
        .flatMap((value) => value.split(/[\s,;]+/))
        .map((value) => value.trim())
        .filter(Boolean)
    )
  );

const getErrorMessage = (error: unknown) => {
  if (typeof error === "object" && error !== null && "data" in error) {
    const data = (error as { data?: { message?: string } }).data;
    if (data?.message) return data.message;
  }
  return "";
};

type FieldStatus = "idle" | "dirty" | "saved" | "error";

interface FieldLabelProps {
  label: React.ReactNode;
  required?: boolean;
  status: FieldStatus;
  onRevert?: () => void;
}

/** Field label with its save status: modified (with undo) or just saved. */
const FieldLabel: React.FC<FieldLabelProps> = ({
  label,
  required,
  status,
  onRevert,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 28 }}>
      <Typography
        sx={{
          fontFamily: "Nunito",
          fontWeight: 700,
          color: theme.palette.grey[900],
        }}
      >
        {label}
        {required && (
          <Box
            component="span"
            sx={{ color: theme.palette.error.main, ml: 0.5 }}
          >
            *
          </Box>
        )}
      </Typography>
      <Box sx={{ flex: 1 }} />
      {status === "dirty" && (
        <Stack direction="row" alignItems="center" spacing={0.25}>
          <Chip
            size="small"
            label={t("company.editForm.fieldModified")}
            sx={{
              height: 20,
              fontSize: 11,
              fontWeight: 700,
              backgroundColor: alpha(theme.palette.warning.main, 0.16),
              color: theme.palette.warning.dark,
            }}
          />
          {onRevert && (
            <Tooltip title={t("company.editForm.revertField")}>
              <IconButton
                size="small"
                onClick={onRevert}
                aria-label={t("company.editForm.revertField")}
                sx={{ p: 0.25, color: theme.palette.warning.dark }}
              >
                <UndoIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}
      <Fade in={status === "saved"} unmountOnExit>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ color: theme.palette.success.main }}
        >
          <CheckCircleIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {t("company.editForm.fieldSaved")}
          </Typography>
        </Stack>
      </Fade>
    </Box>
  );
};

const EditCompany: React.FC = () => {
  const collapsed = useSelector(selectSidebarCollapsed);
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();

  const { data: companyData, isLoading: isLoadingCompany } =
    useGetCompanyByIdQuery(id!);
  const [updateCompany, { isLoading: isUpdating }] =
    useUpdateCompanyMutation();
  const [deleteCompany, { isLoading: isDeleting }] =
    useDeleteCompanyMutation();
  const [assignUsersToCompany, { isLoading: isAssigning }] =
    useAssignUsersToCompanyMutation();
  const [removeUsersFromCompany] = useRemoveUsersFromCompanyMutation();
  const { data: usersData } = useGetUsersQuery();

  const [uploading, setUploading] = useState(false);
  // What the server has (baseline) vs. what the form shows (formData)
  const [baseline, setBaseline] = useState<CompanyFormData>(EMPTY_FORM);
  const [formData, setFormData] = useState<CompanyFormData>(EMPTY_FORM);
  // Fields saved by the last update, flagged for a few seconds
  const [recentlySaved, setRecentlySaved] = useState<TrackedField[]>([]);
  const [saveFailed, setSaveFailed] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);

  // Required fields left empty on the last submit attempt, highlighted in the form
  const [missingRequiredFields, setMissingRequiredFields] = useState<
    RequiredField[]
  >([]);

  const [previewImage, setPreviewImage] = useState<string>();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  const dirtyFields = useMemo(
    () =>
      TRACKED_FIELDS.filter(
        (field) => !isSameValue(field, formData[field], baseline[field])
      ),
    [formData, baseline]
  );
  const isDirty = dirtyFields.length > 0;
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  // IDs used by more than one integration
  const duplicateIds = useMemo(() => {
    const counts = new Map<string, number>();
    INTEGRATION_FIELDS.forEach(({ key }) =>
      (formData[key] ?? []).forEach((integrationId) =>
        counts.set(integrationId, (counts.get(integrationId) ?? 0) + 1)
      )
    );
    return new Set(
      Array.from(counts)
        .filter(([, count]) => count > 1)
        .map(([integrationId]) => integrationId)
    );
  }, [formData]);

  const hasDuplicate = (field: IntegrationField) =>
    (formData[field] ?? []).some((integrationId) =>
      duplicateIds.has(integrationId)
    );

  const fieldStatus = (field: TrackedField, hasError = false): FieldStatus => {
    if (hasError) return "error";
    if (dirtyFields.includes(field)) return "dirty";
    if (recentlySaved.includes(field)) return "saved";
    return "idle";
  };

  const statusColor = (status: FieldStatus) =>
    ({
      idle: theme.palette.neutral[400],
      dirty: theme.palette.warning.main,
      saved: theme.palette.success.main,
      error: theme.palette.error.main,
    })[status];

  const inputSx = (status: FieldStatus, borderRadius: number | string = 50) => ({
    "& .MuiOutlinedInput-root": {
      borderRadius,
      fontFamily: "Nunito",
      fontSize: 16,
      transition: "background-color .3s ease",
      backgroundColor:
        status === "dirty"
          ? alpha(theme.palette.warning.main, 0.06)
          : status === "saved"
            ? alpha(theme.palette.success.main, 0.06)
            : undefined,
      "& fieldset": {
        borderColor: statusColor(status),
        borderWidth: status === "idle" ? 1 : 2,
        transition: "border-color .3s ease",
      },
    },
    "& .MuiFormHelperText-root": {
      fontFamily: "Nunito",
      ml: 2,
    },
  });

  const fieldLabelText = (field: TrackedField) => {
    const [key, fallback] = FIELD_LABELS[field];
    return t(key, fallback);
  };

  // Get assigned users from company data
  const companyUsers = companyData?.data?.users || [];

  // Filter available users (users not already assigned to the company)
  const assignedUserIds = companyUsers.map((cu) => cu.user.id);
  const filteredUsers =
    usersData?.data?.filter(
      (user) =>
        (user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase())) &&
        !assignedUserIds.includes(user.id)
    ) || [];

  // Handle user selection for assignment
  const handleUserSelect = (userId: string) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  // Handle assigning users to company
  const handleAssignUsers = async () => {
    if (selectedUsers.length === 0) return;

    try {
      await assignUsersToCompany({
        id: id!,
        userIds: selectedUsers,
      }).unwrap();

      // Clear selected users after successful assignment
      setSelectedUsers([]);

      dispatch(
        setToastMessage({
          message: t("company.success.assignUsersSuccess"),
          severity: "success",
        })
      );
    } catch (error) {
      Sentry.captureException(error);
      dispatch(
        setToastMessage({
          message: t("company.errors.assignUsersError", {
            error: getErrorMessage(error) || t("common.unknownError"),
          }),
          severity: "error",
        })
      );
    }
  };

  // Handle removing users from company
  const handleRemoveUsers = async (userIds: string[]) => {
    if (userIds.length === 0) return;

    try {
      await removeUsersFromCompany({
        id: id!,
        userIds,
      }).unwrap();

      dispatch(
        setToastMessage({
          message: t("company.success.removeUsersSuccess"),
          severity: "success",
        })
      );
    } catch (error) {
      Sentry.captureException(error);
      dispatch(
        setToastMessage({
          message: t("company.errors.removeUsersError", {
            error: getErrorMessage(error) || t("common.unknownError"),
          }),
          severity: "error",
        })
      );
    }
  };

  // The company is refetched after any change on the page (billing, users…).
  // The baseline always follows the server; the form only while it has no
  // unsaved changes, so a refetch never wipes what the user is editing.
  useEffect(() => {
    const company = companyData?.data;
    if (!company) return;

    const next = toFormData(company);
    setBaseline(next);
    if (!isDirtyRef.current) {
      setFormData(next);
      setPreviewImage(
        company.imageBase64
          ? `data:image/png;base64,${company.imageBase64}`
          : undefined
      );
    }
  }, [companyData]);

  useEffect(() => {
    if (recentlySaved.length === 0) return;
    const handle = setTimeout(() => setRecentlySaved([]), 3000);
    return () => clearTimeout(handle);
  }, [recentlySaved]);

  // Warn before closing or reloading the tab with unsaved changes
  useEffect(() => {
    if (!isDirty) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files?.[0]) {
      setUploading(true);
      try {
        const file = event.target.files[0];
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          const base64WithoutPrefix = base64String.split(",")[1];
          setFormData((prev) => ({
            ...prev,
            imageBase64: base64WithoutPrefix,
          }));
          setPreviewImage(base64String);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error uploading image:", error);
        dispatch(
          setToastMessage({
            message: t("company.errors.imageUploadError"),
            severity: "error",
          })
        );
      } finally {
        setUploading(false);
        // Allows picking the same file again after a revert
        event.target.value = "";
      }
    }
  };

  const resetPreview = (imageBase64?: string) =>
    setPreviewImage(
      imageBase64 ? `data:image/png;base64,${imageBase64}` : undefined
    );

  const revertField = (field: TrackedField) => {
    setFormData((prev) => ({ ...prev, [field]: baseline[field] }));
    if (field === "imageBase64") resetPreview(baseline.imageBase64);
  };

  const handleDiscard = () => {
    setFormData(baseline);
    resetPreview(baseline.imageBase64);
    setMissingRequiredFields([]);
    setSaveFailed(false);
  };

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!id) {
      dispatch(
        setToastMessage({
          message: t("common.errors.missingId", {
            defaultValue: "Company ID is missing",
          }),
          severity: "error",
        })
      );
      return;
    }
    if (!isDirty || isUpdating) return;

    const missingFields = REQUIRED_FIELDS.filter(
      (field) => !String(formData[field] ?? "").trim()
    );

    if (missingFields.length > 0) {
      setMissingRequiredFields(missingFields);

      dispatch(
        setToastMessage({
          message: t("company.errors.missingFieldsDetail", {
            fields: missingFields
              .map((field) => t(`company.${field}`))
              .join(", "),
            defaultValue:
              "Please fill in all required fields before submitting",
          }),
          severity: "error",
        })
      );

      document
        .getElementById(`company-field-${missingFields[0]}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });

      return;
    }

    setMissingRequiredFields([]);

    if (duplicateIds.size > 0) {
      dispatch(
        setToastMessage({
          message: t("company.editForm.duplicateIds"),
          severity: "error",
        })
      );

      const firstDuplicate = INTEGRATION_FIELDS.find(({ key }) =>
        hasDuplicate(key)
      );
      document
        .getElementById(`company-field-${firstDuplicate?.key}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });

      return;
    }

    const billings = companyData?.data?.billings ?? [];
    const defaultBilling = billings.find((b) => b.isDefault) || billings[0];

    // Create a clean object with only the company fields
    // Explicitly excluding users, id, createdAt, and updatedAt
    const updateData: Partial<Company> = {
      nameCommercial: formData.nameCommercial?.trim(),
      organizationTypes: formData.organizationTypes ?? [],
      nameFiscal: formData.nameFiscal?.trim(),
      nif: formData.nif?.trim(),
      logoUrl: formData.logoUrl || "",
      ...(Object.fromEntries(
        INTEGRATION_FIELDS.map(({ key }) => [key, formData[key] || []])
      ) as Record<IntegrationField, string[]>),
      // Duplican lo que ya vive en el billing por defecto, así que se copian de
      // allí en lugar de pedirse otra vez: así no pueden quedar descuadrados.
      email: defaultBilling?.email ?? "",
      phone: defaultBilling?.phoneNumber ?? "",
      feeVariable: parseFee(formData.feeVariable),
      feeFixed: parseFee(formData.feeFixed),
      paymentTerms: formData.paymentTerms || "",
    };

    // Only send the image when a new one was uploaded
    if (dirtyFields.includes("imageBase64") && formData.imageBase64) {
      updateData.imageBase64 = formData.imageBase64;
    }

    const submitted = formData;
    const savedFields = dirtyFields;

    try {
      await updateCompany({ id, updates: updateData }).unwrap();
      // Edits typed while saving stay pending against the new baseline
      setBaseline(submitted);
      setRecentlySaved(savedFields);
      setSaveFailed(false);
      dispatch(
        setToastMessage({
          message: t("company.success.updateSuccess"),
          severity: "success",
        })
      );
    } catch (error) {
      Sentry.captureException(error);
      setSaveFailed(true);
      dispatch(
        setToastMessage({
          message: t("company.errors.updateError", {
            error: getErrorMessage(error),
          }),
          severity: "error",
        })
      );
    }
  };

  // Ctrl/Cmd+S saves. Blurring first commits an ID still being typed.
  const submitRef = useRef(handleSubmit);
  submitRef.current = handleSubmit;
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        (document.activeElement as HTMLElement | null)?.blur();
        setTimeout(() => void submitRef.current());
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteCompany(id).unwrap();
      setDeleteDialogOpen(false);
      dispatch(
        setToastMessage({
          message: t("company.success.deleteSuccess"),
          severity: "success",
        })
      );
      navigate("/companies");
    } catch (error) {
      Sentry.captureException(error);
      dispatch(
        setToastMessage({
          message: t("company.errors.deleteError", {
            error: getErrorMessage(error),
          }),
          severity: "error",
        })
      );
    }
  };

  const requestLeave = () => {
    if (isDirty) {
      setLeaveDialogOpen(true);
    } else {
      goBack("/companies");
    }
  };

  const handleInputChange =
    (field: keyof CompanyFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;

      setFormData((prev) => ({
        ...prev,
        [field]: newValue,
      }));

      if (newValue.trim()) {
        setMissingRequiredFields((previous) =>
          previous.filter((missingField) => missingField !== field)
        );
      }
    };

  const handleIntegrationChange = (field: IntegrationField, values: string[]) =>
    setFormData((prev) => ({ ...prev, [field]: splitIds(values) }));

  const renderRequiredField = (field: RequiredField) => {
    const isMissing = missingRequiredFields.includes(field);
    const status = fieldStatus(field, isMissing);
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <FieldLabel
          label={fieldLabelText(field)}
          required
          status={status}
          onRevert={() => revertField(field)}
        />
        <TextField
          id={`company-field-${field}`}
          value={formData[field]}
          onChange={handleInputChange(field)}
          fullWidth
          variant="outlined"
          error={isMissing}
          helperText={isMissing ? t("company.errors.requiredField") : ""}
          inputProps={{ "aria-required": true }}
          sx={inputSx(status)}
        />
      </Box>
    );
  };

  const renderIntegrationField = ({
    key,
  }: (typeof INTEGRATION_FIELDS)[number]) => {
    const duplicated = hasDuplicate(key);
    const status = fieldStatus(key, duplicated);
    const savedIds = baseline[key] ?? [];
    return (
      <Box
        key={key}
        sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}
      >
        <FieldLabel
          label={fieldLabelText(key)}
          status={status}
          onRevert={() => revertField(key)}
        />
        <Autocomplete
          multiple
          freeSolo
          // Text left in the input is added as an ID when the field loses focus
          autoSelect
          options={[]}
          value={formData[key] ?? []}
          onChange={(_, newValue) => handleIntegrationChange(key, newValue)}
          renderTags={(value: readonly string[], getTagProps) =>
            value.map((option: string, index: number) => {
              const { key: tagKey, ...tagProps } = getTagProps({ index });
              const color = duplicateIds.has(option)
                ? "error"
                : savedIds.includes(option)
                  ? "default"
                  : "warning";
              return (
                <Chip
                  variant={color === "default" ? "outlined" : "filled"}
                  color={color}
                  label={option}
                  key={tagKey}
                  {...tagProps}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              id={`company-field-${key}`}
              fullWidth
              error={duplicated}
              placeholder={
                (formData[key] ?? []).length === 0 ? fieldLabelText(key) : ""
              }
              sx={inputSx(status, "15px")}
            />
          )}
        />
        {duplicated && (
          <FormHelperText error sx={{ ml: 2 }}>
            {t("company.editForm.duplicateId")}
          </FormHelperText>
        )}
      </Box>
    );
  };

  type BarState = "saving" | "error" | "dirty" | "saved" | "clean";
  const barState: BarState = isUpdating
    ? "saving"
    : isDirty
      ? saveFailed
        ? "error"
        : "dirty"
      : recentlySaved.length > 0
        ? "saved"
        : "clean";

  const barColor = {
    saving: theme.palette.info.main,
    error: theme.palette.error.main,
    dirty: theme.palette.warning.main,
    saved: theme.palette.success.main,
    clean: theme.palette.neutral[400],
  }[barState];

  const barIcon = {
    saving: <CircularProgress size={20} sx={{ color: barColor }} />,
    error: <ErrorOutlineIcon sx={{ color: barColor }} />,
    dirty: (
      <Box
        sx={{
          width: 12,
          height: 12,
          m: "4px",
          borderRadius: "50%",
          backgroundColor: barColor,
          "@keyframes unsavedPulse": {
            "0%": { boxShadow: `0 0 0 0 ${alpha(barColor, 0.6)}` },
            "70%": { boxShadow: `0 0 0 8px ${alpha(barColor, 0)}` },
            "100%": { boxShadow: `0 0 0 0 ${alpha(barColor, 0)}` },
          },
          animation: "unsavedPulse 1.8s infinite",
        }}
      />
    ),
    saved: <CheckCircleIcon sx={{ color: barColor }} />,
    clean: <CheckCircleOutlineIcon sx={{ color: barColor }} />,
  }[barState];

  const barTitle = {
    saving: t("company.editForm.saving"),
    error: t("company.editForm.saveFailed"),
    dirty: t("company.editForm.unsaved", { count: dirtyFields.length }),
    saved: t("company.editForm.saved"),
    clean: t("company.editForm.clean"),
  }[barState];

  if (isLoadingCompany) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          marginLeft: collapsed ? "80px" : "250px",
        }}
      >
        <OrbLoader
          size="large"
          label={t("common.loading", { defaultValue: "Cargando…" })}
        />
      </Box>
    );
  }

  const logoStatus = fieldStatus("imageBase64");

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Box
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
          display: "flex",
          flexDirection: "column",
          width: { xs: "calc(100% - 80px)", sm: "auto" },
          maxWidth: "100%",
          boxSizing: "border-box",
          // "clip" instead of "hidden": hidden would turn this box into a
          // scroll container and break the sticky save bar
          overflowX: "clip",
          minHeight: "100vh",
        }}
      >
        <PageBreadcrumbs />

        <PageContainer>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: 0 },
                mb: 4,
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.5,
                  backgroundColor: BRAND_PILL_INK,
                  borderRadius: "16px",
                  px: 3,
                  py: 1.5,
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                <Typography
                  variant="h3"
                  noWrap
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.secondary50.main,
                  }}
                >
                  {formData.nameCommercial}
                </Typography>
                <EditIcon sx={{ color: theme.palette.secondary50.main }} />
              </Box>
              <Button
                startIcon={<KeyboardBackspaceIcon />}
                onClick={requestLeave}
                sx={{
                  height: "fit-content",
                  borderRadius: 50,
                  textTransform: "none",
                  fontFamily: "Nunito",
                  fontWeight: 700,
                  px: 3,
                  py: 1.5,
                  borderColor: theme.palette.grey[200],
                  color: theme.palette.grey[900],
                  "&:hover": {
                    borderColor: theme.palette.grey[300],
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
              >
                {t("common.back")}
              </Button>
            </Box>

            {/* Logo Upload Section */}
            <Box sx={{ mb: 4 }}>
              <FormControl>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      borderRadius: 9999,
                      border: `${logoStatus === "idle" ? 2 : 3}px solid ${
                        logoStatus === "idle"
                          ? theme.palette.grey[200]
                          : statusColor(logoStatus)
                      }`,
                      transition: "border-color .3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      backgroundColor: theme.palette.grey[100],
                    }}
                  >
                    {uploading ? (
                      <OrbLoader size="medium" />
                    ) : previewImage ? (
                      <Box
                        component="img"
                        src={previewImage}
                        alt={formData.nameCommercial || t("company.logo")}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <PhotoCamera
                        sx={{ color: theme.palette.grey[400], fontSize: 40 }}
                      />
                    )}
                  </Box>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: "none" }}
                      id="company-logo-upload"
                      disabled={uploading}
                    />
                    <label htmlFor="company-logo-upload">
                      <Button
                        component="span"
                        variant="outlined"
                        disabled={uploading}
                        sx={{
                          borderRadius: 50,
                          textTransform: "none",
                          fontFamily: "Nunito",
                          fontWeight: 700,
                          px: 3,
                          py: 1.5,
                          borderColor: theme.palette.grey[200],
                          color: theme.palette.grey[900],
                          "&:hover": {
                            borderColor: theme.palette.grey[300],
                            backgroundColor: theme.palette.background.paper,
                          },
                        }}
                      >
                        {uploading ? (
                          <CircularProgress size={24} />
                        ) : (
                          t("company.uploadLogo")
                        )}
                      </Button>
                    </label>
                    {logoStatus !== "idle" && (
                      <FieldLabel
                        label=""
                        status={logoStatus}
                        onRevert={() => revertField("imageBase64")}
                      />
                    )}
                  </Box>
                </Box>
              </FormControl>
            </Box>

            {/* Company Details Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                backgroundColor: theme.palette.neutral["100"],
                borderRadius: "8px",
                p: 3,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                {renderRequiredField("nameCommercial")}
                <OrganizationTypesField
                  value={formData.organizationTypes ?? []}
                  onChange={(next) =>
                    setFormData((prev) => ({
                      ...prev,
                      organizationTypes: next,
                    }))
                  }
                  label={
                    <FieldLabel
                      label={fieldLabelText("organizationTypes")}
                      status={fieldStatus("organizationTypes")}
                      onRevert={() => revertField("organizationTypes")}
                    />
                  }
                  borderColor={
                    fieldStatus("organizationTypes") === "idle"
                      ? undefined
                      : statusColor(fieldStatus("organizationTypes"))
                  }
                />
                {renderRequiredField("nameFiscal")}
                {renderRequiredField("nif")}

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <FieldLabel
                    label={fieldLabelText("feeVariable")}
                    status={fieldStatus("feeVariable")}
                    onRevert={() => revertField("feeVariable")}
                  />
                  <NumberField
                    value={formData.feeVariable}
                    onChange={handleInputChange("feeVariable")}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                      inputProps: {
                        min: 0,
                        max: 100,
                        step: "0.01",
                      },
                    }}
                    sx={inputSx(fieldStatus("feeVariable"))}
                  />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <FieldLabel
                    label={fieldLabelText("feeFixed")}
                    status={fieldStatus("feeFixed")}
                    onRevert={() => revertField("feeFixed")}
                  />
                  <NumberField
                    value={formData.feeFixed}
                    onChange={handleInputChange("feeFixed")}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        step: "0.01",
                      },
                    }}
                    sx={inputSx(fieldStatus("feeFixed"))}
                  />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <FieldLabel
                    label={fieldLabelText("paymentTerms")}
                    status={fieldStatus("paymentTerms")}
                    onRevert={() => revertField("paymentTerms")}
                  />
                  <TextField
                    select
                    value={formData.paymentTerms ?? ""}
                    onChange={(event) =>
                      setFormData((prev) => ({
                        ...prev,
                        paymentTerms: event.target.value,
                      }))
                    }
                    fullWidth
                    variant="outlined"
                    sx={inputSx(fieldStatus("paymentTerms"))}
                  >
                    <MenuItem value="">
                      {t("company.paymentTermsUndefined")}
                    </MenuItem>
                    <MenuItem value="30">30</MenuItem>
                    <MenuItem value="45">45</MenuItem>
                    <MenuItem value="60">60</MenuItem>
                  </TextField>
                </Box>
              </Box>
            </Box>

            {/* Billing Management Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                backgroundColor: theme.palette.neutral["100"],
                borderRadius: "8px",
                p: 3,
              }}
            >
              {id && <BillingManagement companyId={id} />}
            </Box>

            {/* Audiences Section */}
            {id && <AudienceManagement companyId={id} />}

            {/* Integrations Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                backgroundColor: theme.palette.neutral["100"],
                borderRadius: "8px",
                p: 3,
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("company.integrations")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "Nunito", color: theme.palette.grey[600] }}
                >
                  {t("company.editForm.idsHelper")}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(3, minmax(0, 1fr))",
                  },
                }}
              >
                {INTEGRATION_FIELDS.map(renderIntegrationField)}
              </Box>
            </Box>

            {/* User Assignment Section */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Nunito",
                  fontWeight: 700,
                  color: theme.palette.grey[900],
                  pb: 1,
                  mb: 1,
                }}
              >
                {t("company.assignUsers")}
              </Typography>

              {/* Search and Assign Users */}
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                    mb: 1,
                  }}
                >
                  {t("company.searchUsers")}
                </Typography>

                <TextField
                  placeholder={t("company.searchUsers")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  fullWidth
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 50,
                      fontFamily: "Nunito",
                      fontSize: 16,
                      "& fieldset": {
                        borderColor: theme.palette.neutral[400],
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                {searchTerm.length > 0 && (
                  <Paper
                    variant="outlined"
                    sx={{
                      maxHeight: 300,
                      overflow: "auto",
                      borderRadius: 2,
                      mt: 2,
                    }}
                  >
                    {filteredUsers.length > 0 ? (
                      <List dense>
                        {filteredUsers.map((user) => (
                          <ListItem
                            key={user.id}
                            onClick={() => handleUserSelect(user.id)}
                            sx={{
                              "&:hover": {
                                backgroundColor: theme.palette.grey[100],
                              },
                              cursor: "pointer",
                            }}
                          >
                            <ListItemIcon>
                              <Checkbox
                                edge="start"
                                checked={selectedUsers.includes(user.id)}
                                tabIndex={-1}
                                disableRipple
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography
                                  sx={{ fontFamily: "Nunito", fontWeight: 600 }}
                                >
                                  {`${user.firstName} ${user.lastName}`}
                                </Typography>
                              }
                              secondary={
                                <Typography
                                  variant="body2"
                                  sx={{ color: theme.palette.grey[600] }}
                                >
                                  {user.email}
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Box
                        sx={{
                          p: 3,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{ fontFamily: "Nunito", textAlign: "center" }}
                        >
                          {t("company.noUsersFound")}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                )}

                {selectedUsers.length > 0 && (
                  <Button
                    variant="contained"
                    onClick={handleAssignUsers}
                    disabled={isAssigning}
                    startIcon={
                      isAssigning ? null : <i className="fas fa-user-plus" />
                    }
                    sx={{
                      borderRadius: 50,
                      textTransform: "none",
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      px: 3,
                      py: 1.5,
                      mt: 2,
                    }}
                  >
                    {isAssigning ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      `${t("company.assignSelectedUsers")} (${
                        selectedUsers.length
                      })`
                    )}
                  </Button>
                )}
              </Box>

              {/* Assigned Users */}
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                    mb: 2,
                  }}
                >
                  {t("company.assignedUsers")}
                </Typography>

                {companyUsers.length > 0 ? (
                  <>
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ maxHeight: 300, overflow: "auto", borderRadius: 2 }}
                    >
                      <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                            >
                              {t("company.name")}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                            >
                              {t("company.email")}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                            ></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {companyUsers.map((user) => (
                            <TableRow
                              key={user.user.id}
                              sx={{
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                              }}
                            >
                              <TableCell component="th" scope="row">
                                <Typography
                                  sx={{ fontFamily: "Nunito", fontWeight: 600 }}
                                >
                                  {`${user.user.firstName} ${user.user.lastName}`}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{ color: theme.palette.grey[600] }}
                                >
                                  {user.user.email}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="text"
                                  onClick={() =>
                                    handleRemoveUsers([user.user.id])
                                  }
                                  startIcon={<DeleteIcon />}
                                  sx={{
                                    color: theme.palette.error.main,
                                    textTransform: "none",
                                    fontFamily: "Nunito",
                                    fontWeight: 700,
                                  }}
                                >
                                  {t("company.remove")}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      sx={{ fontFamily: "Nunito", textAlign: "center" }}
                    >
                      {t("company.noUsersAssigned")}
                    </Typography>
                  </Paper>
                )}
              </Box>
            </Box>

            {/* Actions */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                mt: 2,
              }}
            >
              <Button
                variant="text"
                onClick={requestLeave}
                startIcon={<KeyboardBackspaceIcon />}
                sx={{
                  color: theme.palette.grey[900],
                  textTransform: "none",
                  fontFamily: "Nunito",
                  fontWeight: 700,
                }}
              >
                {t("common.back")}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setDeleteDialogOpen(true)}
                startIcon={<DeleteIcon />}
                disabled={isDeleting}
                sx={{
                  borderRadius: 50,
                  textTransform: "none",
                  fontFamily: "Nunito",
                  fontWeight: 700,
                  px: 3,
                  py: 1.5,
                }}
              >
                {t("company.delete")}
              </Button>
            </Box>

            {/* Sticky save bar: always visible, shows the save status */}
            <Paper
              elevation={0}
              role="status"
              aria-live="polite"
              sx={{
                position: "sticky",
                bottom: 16,
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                px: 2.5,
                py: 1.5,
                borderRadius: "16px",
                border: `1px solid ${alpha(barColor, 0.5)}`,
                borderLeft: `6px solid ${barColor}`,
                backgroundColor: theme.palette.background.paper,
                boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.12)",
                transition: "border-color .3s ease",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  minWidth: 0,
                  flex: 1,
                }}
              >
                {barIcon}
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {barTitle}
                  </Typography>
                  {isDirty && (
                    <Typography
                      variant="body2"
                      noWrap
                      title={dirtyFields.map(fieldLabelText).join(", ")}
                      sx={{
                        fontFamily: "Nunito",
                        color: theme.palette.grey[600],
                      }}
                    >
                      {dirtyFields.map(fieldLabelText).join(", ")}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {isDirty && !isUpdating && (
                  <Button
                    variant="text"
                    onClick={handleDiscard}
                    sx={{
                      borderRadius: 50,
                      textTransform: "none",
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[700],
                    }}
                  >
                    {t("company.editForm.discard")}
                  </Button>
                )}
                <Tooltip title={t("company.editForm.shortcutHint")}>
                  <span>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={!isDirty || isUpdating}
                      startIcon={
                        isUpdating ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : isDirty ? (
                          <SaveIcon />
                        ) : (
                          <CheckCircleIcon />
                        )
                      }
                      sx={{
                        borderRadius: 50,
                        textTransform: "none",
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        px: 3,
                        py: 1.25,
                        backgroundColor: theme.palette.grey[900],
                        color: theme.palette.getContrastText(
                          theme.palette.grey[900]
                        ),
                        "&:hover": {
                          backgroundColor: theme.palette.grey[800],
                        },
                        "&.Mui-disabled": {
                          backgroundColor: isUpdating
                            ? theme.palette.grey[700]
                            : alpha(theme.palette.success.main, 0.12),
                          color: isUpdating
                            ? theme.palette.common.white
                            : theme.palette.success.dark,
                        },
                      }}
                    >
                      {isUpdating
                        ? t("company.editForm.savingButton")
                        : isDirty
                          ? t("company.updateCompany")
                          : t("company.editForm.savedButton")}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Paper>
          </Box>
        </PageContainer>
      </Box>

      <ConfirmationModal
        open={deleteDialogOpen}
        title={t("company.editForm.deleteTitle")}
        message={t("company.editForm.deleteMessage", {
          name: formData.nameCommercial,
        })}
        confirmLabel={t("company.delete")}
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
        isLoading={isDeleting}
      />
      <ConfirmationModal
        open={leaveDialogOpen}
        title={t("company.editForm.leaveTitle")}
        message={t("company.editForm.leaveMessage")}
        confirmLabel={t("company.editForm.leaveConfirm")}
        cancelLabel={t("company.editForm.stay")}
        onConfirm={() => {
          setLeaveDialogOpen(false);
          goBack("/companies");
        }}
        onCancel={() => setLeaveDialogOpen(false)}
      />
    </Box>
  );
};

export default EditCompany;
